// @flow
import React, { useState } from 'react';
import { BlockPicker, ChromePicker } from 'react-color';
import { defineMessages } from 'react-intl';
import * as Yup from 'yup';
import {
    Layer, TextInput, ThemeContext,
} from 'grommet';
import FieldWrapper from 'speed-js-react-web/src/components/grommet2/forms/fields/FieldWrapper';

type Props = {
    field: {
        value: any;
        name: string;
    },
    form: {
        setFieldValue: (string, any) => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    type: string,
}

const TYPES = {
    small: BlockPicker,
    full: ChromePicker,
};

const ColorPicker = (props: Props) => {
    const {
        field: {
            name, value,
        },
        form: { setFieldValue },
        label,
        help_text: help,
        required,
        type,
    } = props;

    const Picker = TYPES[type];

    const [showPicker, setShowPicker] = useState(false);
    const [displayValue, setDisplayValue] = useState(value);

    React.useEffect(() => {
        if (displayValue !== value) {
            setDisplayValue(value);
        }
    }, [value]);

    const handleClose = () => setShowPicker(false);
    const handleChange = (color) => {
        setFieldValue(name, color.hex);
        setDisplayValue(color.hex);
    };

    const colorBox = (
        <ThemeContext.Extend
            value={{
                textInput: {
                    extend: (inputProps: Object) => {
                        let css = 'height: 32px; ';

                        if (inputProps.value) {
                            css += `background-color: ${inputProps.value};`;
                        }
                        return css;
                    },
                },
            }}
        >
            <TextInput
                value={displayValue}
                onClick={() => { setShowPicker(true); }}
            />
        </ThemeContext.Extend>
    );

    const picker = (
        <Layer
            position="center"
            modal
            onClickOutside={handleClose}
            onEsc={handleClose}
        >
            <Picker color={displayValue || '#bfcc31'} onChange={handleChange} />
        </Layer>
    );

    return (
        <FieldWrapper
            name={name}
            required={required}
            helpText={help}
            label={label}
        >
            {colorBox}
            {showPicker && picker}
        </FieldWrapper>
    );
};

const messages = defineMessages({
    required: {
        id: 'colorpicker-required',
        defaultMessage: 'This field is required.',
    },
});

ColorPicker.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required(messages.required).nullable();
    }

    return Yup.string().nullable();
};

ColorPicker.defaultProps = {
    type: 'full',
};

export default ColorPicker;
